import type { StatementNodeAttributes, StatementNodeData, StatementNodeType, StatementSegment, StatementSentenceRow } from './types';

let rowSequence = 0;
const nextLocalRowId = (): string => {
  rowSequence += 1;
  return `row-local-${rowSequence}`;
};

const parseBoolean = (value: string | undefined): boolean | undefined => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
};

const trimOuterWhitespace = (value: string): string => value.replace(/^\s+|\s+$/g, '');

const withRowId = (row: Omit<StatementSentenceRow, 'rowId'>, rowId: string): StatementSentenceRow => ({
  rowId,
  ...row,
});

export const segmentsToSentenceRows = (segments: StatementSegment[]): StatementSentenceRow[] => {
  const rows: StatementSentenceRow[] = [];

  segments.forEach((segment, segmentIndex) => {
    if (segment.kind === 'node') {
      rows.push(
        withRowId({
          sourceKind: 'node',
          text: segment.node.text,
          nodeType: segment.node.type,
          id: segment.node.id,
          code: segment.node.code,
          groupId: segment.node.groupId,
          hasVariable: segment.node.hasVariable,
          variantId: segment.node.variantId,
          isEdited: segment.node.isEdited,
          attributes: { ...segment.node.attributes },
        }, `row-node-${segmentIndex}-${segment.node.id || segment.node.groupId || segment.node.code || 'plain'}`)
      );
      return;
    }

    const text = trimOuterWhitespace(segment.text);
    if (!text) return;

    rows.push(
      withRowId({
        sourceKind: 'text',
        text,
        attributes: {},
      }, `row-text-${segmentIndex}`)
    );
  });

  return rows;
};

const inferNodeType = (row: StatementSentenceRow): StatementNodeType => {
  if (row.nodeType) return row.nodeType;
  if (row.code) return 'GlossaryStatementNode';
  return 'StatementNode';
};

const toNodeData = (row: StatementSentenceRow): StatementNodeData => {
  const nextType = inferNodeType(row);
  const isEdited = row.isEdited === true;
  const normalizedVariantId = (row.variantId || '').trim();
  const resolvedVariantId = nextType === 'GlossaryStatementNode' ? (normalizedVariantId || '0') : '';
  const nextAttributes: StatementNodeAttributes = {
    ...row.attributes,
    isEdited: isEdited ? 'true' : 'false',
  };

  if (row.id) nextAttributes.id = row.id;
  else delete nextAttributes.id;

  if (row.code) nextAttributes.code = row.code;
  else delete nextAttributes.code;

  if (row.groupId) nextAttributes.groupId = row.groupId;
  else delete nextAttributes.groupId;

  if (typeof row.hasVariable === 'boolean') nextAttributes.hasVariable = row.hasVariable ? 'true' : 'false';
  else delete nextAttributes.hasVariable;

  if (nextType === 'GlossaryStatementNode') nextAttributes.variantId = resolvedVariantId;
  else delete nextAttributes.variantId;

  return {
    type: nextType,
    text: row.text,
    attributes: nextAttributes,
    id: row.id,
    code: row.code,
    groupId: row.groupId,
    hasVariable: row.hasVariable,
    variantId: nextType === 'GlossaryStatementNode' ? resolvedVariantId : undefined,
    isEdited,
  };
};

const separatorBetween = (prev: StatementSentenceRow, next: StatementSentenceRow): string => {
  if (prev.text.endsWith('\n') || next.text.startsWith('\n')) return '';
  return ' ';
};

export const sentenceRowsToSegments = (rows: StatementSentenceRow[]): StatementSegment[] => {
  const normalized = rows;
  const segments: StatementSegment[] = [];

  normalized.forEach((row, index) => {
    const isNode = row.sourceKind === 'node' || !!row.code || !!row.nodeType;

    if (isNode) {
      segments.push({
        kind: 'node',
        node: toNodeData(row),
      });
    } else {
      segments.push({
        kind: 'text',
        text: row.text,
      });
    }

    const next = normalized[index + 1];
    if (!next) return;

    const separator = separatorBetween(row, next);
    if (separator) segments.push({ kind: 'text', text: separator });
  });

  return segments;
};

export const sentenceRowsToPlainText = (rows: StatementSentenceRow[]): string => {
  return sentenceRowsToSegments(rows)
    .map((segment) => (segment.kind === 'text' ? segment.text : segment.node.text))
    .join('');
};

export const createEmptySentenceRow = (): StatementSentenceRow =>
  withRowId({
    sourceKind: 'node',
    text: '',
    nodeType: 'StatementNode',
    attributes: {
      isEdited: 'true',
    },
    isEdited: true,
  }, nextLocalRowId());

export const parseRowEditedFlag = (row: StatementSentenceRow): boolean => {
  if (typeof row.isEdited === 'boolean') return row.isEdited;
  return parseBoolean(row.attributes.isEdited) === true;
};
